import {
  groupBudget,
  serviceLockKey,
  summarizeBudget,
  type BudgetLine,
  type BudgetTotals,
  type ServiceApproval,
} from '@obraplan/core/domain/takeoff/project-budget';
import type { XlsxCell, XlsxSheet } from '@/features/reports/xlsx';
import { mesPorExtenso } from '@/features/workforce/custo-da-folha';
import { formatBusinessDate } from '@/lib/format';
import { ROTULO_DA_ORIGEM } from './orcamento-da-obra';

/**
 * O orçamento da obra como planilha: o detalhe disciplina → serviço → item numa aba, e o
 * resumo por disciplina na outra (Fase 29, etapa 4 — D15, D17).
 *
 * É o que sai da obra para quem confere o orçamento fora do sistema. Por isso os números saem
 * como **número** — quantidade com as três casas do compras, preço unitário com as quatro de D17,
 * total em reais —, e o Excel de quem abre os escreve na língua dele; texto formatado em pt-BR
 * não somaria. E por isso o subtotal de cada disciplina e de cada serviço vem em negrito, na
 * linha dele, e o fecho — material, mão de obra e custo direto — no pé da aba.
 *
 * A mesma função serve ao orçamento vivo e ao serviço aprovado: ela lê só as linhas, com o total
 * de cada uma, e refaz os subtotais pelas funções do domínio. O subtotal do serviço aprovado diz
 * quando e por quem ele foi aprovado (Fase 33, RN-437).
 */

export interface DadosDoOrcamento {
  /** O nome da obra — vai na aba "Resumo". */
  readonly obra: string;
  /** As linhas na ordem da obra, com o total de cada uma (`BudgetLine`). */
  readonly linhas: readonly BudgetLine[];
  /** Quando a planilha foi gerada. */
  readonly geradoEm: Date;
  /** O período da foto do compras que valia (RN-397), `AAAA-MM-DD`. */
  readonly periodoDosPrecos?: { readonly from: string; readonly to: string };
  /** O mês das horas do R$/h, `AAAA-MM` (RN-399). */
  readonly mesDaMaoDeObra?: string;
  /** Os serviços aprovados, pela chave da trava: quando (ISO) e por quem (Fase 33). */
  readonly aprovados?: ReadonlyMap<string, ServiceApproval>;
}

const CABECALHO = [
  'Disciplina',
  'Serviço',
  'Item',
  'Código',
  'Un.',
  'Quantidade',
  'Preço unit.',
  'Origem',
  'Total',
  'Aviso',
] as const;

/** Os códigos do Excel, em notação neutra: é o Excel de quem abre que escolhe vírgula ou ponto. */
const QUANTIDADE = '#,##0.000';
const PRECO_UNITARIO = '"R$" #,##0.0000';
const DINHEIRO = '"R$" #,##0.00';

const FORMATOS_DO_DETALHE = [
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  QUANTIDADE,
  PRECO_UNITARIO,
  undefined,
  DINHEIRO,
  undefined,
] as const;

const LARGURAS_DO_DETALHE = [24, 30, 46, 14, 8, 14, 16, 18, 16, 44] as const;

const CABECALHO_DO_RESUMO = [
  'Disciplina',
  'Material',
  'Mão de obra',
  'Total',
  'Linhas fora do total',
] as const;

/** `1 linha fora do total`, `3 linhas fora do total` — no subtotal que ficou incompleto. */
function foraDoTotal(quantas: number): string {
  if (quantas === 0) return '';
  return `${String(quantas)} ${quantas === 1 ? 'linha fora' : 'linhas fora'} do total`;
}

/** `13/10/2026 14:30`, no relógio de quem gerou — o instante inválido sai como veio. */
export function dataEHora(instante: Date | string): string {
  const data = typeof instante === 'string' ? new Date(instante) : instante;
  if (Number.isNaN(data.getTime())) return String(instante);
  const dois = (valor: number): string => String(valor).padStart(2, '0');
  return `${dois(data.getDate())}/${dois(data.getMonth() + 1)}/${String(data.getFullYear())} ${dois(data.getHours())}:${dois(data.getMinutes())}`;
}

/** "Subtotal do serviço — aprovado em 19/10/2026 14:30 por Ana", ou só o subtotal. */
function rotuloDoServico(aprovacao: ServiceApproval | undefined): string {
  if (aprovacao === undefined) return 'Subtotal do serviço';
  const por = aprovacao.approvedByName === undefined ? '' : ` por ${aprovacao.approvedByName}`;
  return `Subtotal do serviço — aprovado em ${dataEHora(aprovacao.approvedAt)}${por}`;
}

/** Uma linha de soma: o rótulo no lugar do item e o total na coluna dele. */
function linhaDeSoma(
  disciplina: string,
  servico: string,
  rotulo: string,
  totais: Pick<BudgetTotals, 'total' | 'linesOutOfTotal'>,
): readonly XlsxCell[] {
  return [
    disciplina,
    servico,
    rotulo,
    '',
    '',
    '',
    '',
    '',
    totais.total,
    foraDoTotal(totais.linesOutOfTotal),
  ];
}

/** Uma linha de item: o preço, a origem e o total só quando existem — célula vazia, e não zero. */
function linhaDeItem(linha: BudgetLine): readonly XlsxCell[] {
  const aviso =
    linha.total === undefined
      ? `${linha.warning ?? 'sem preço'} — fora do total`
      : (linha.warning ?? '');
  return [
    linha.disciplineName,
    linha.serviceName,
    linha.description,
    linha.code ?? '',
    linha.unit ?? '',
    linha.quantity,
    linha.unitPrice ?? '',
    linha.priceOrigin === undefined ? '' : ROTULO_DA_ORIGEM[linha.priceOrigin],
    linha.total ?? '',
    aviso,
  ];
}

function abaDoDetalhe(
  linhas: readonly BudgetLine[],
  aprovados: ReadonlyMap<string, ServiceApproval> | undefined,
): XlsxSheet {
  const rows: (readonly XlsxCell[])[] = [];
  const emNegrito: number[] = [];

  for (const disciplina of groupBudget(linhas)) {
    emNegrito.push(rows.length);
    rows.push(linhaDeSoma(disciplina.name, '', 'Subtotal da disciplina', disciplina));
    for (const servico of disciplina.services) {
      emNegrito.push(rows.length);
      rows.push(
        linhaDeSoma(
          disciplina.name,
          servico.name,
          rotuloDoServico(aprovados?.get(serviceLockKey(servico.key))),
          servico,
        ),
      );
      for (const linha of servico.lines) rows.push(linhaDeItem(linha));
    }
  }

  const resumo = summarizeBudget(linhas);
  for (const fecho of [
    linhaDeSoma('Material', '', '', { total: resumo.material, linesOutOfTotal: 0 }),
    linhaDeSoma('Mão de obra', '', '', { total: resumo.labor, linesOutOfTotal: 0 }),
    linhaDeSoma('Custo direto', '', '', resumo),
  ]) {
    emNegrito.push(rows.length);
    rows.push(fecho);
  }

  return {
    name: 'Orçamento',
    headers: CABECALHO,
    rows,
    widths: LARGURAS_DO_DETALHE,
    formats: FORMATOS_DO_DETALHE,
    boldRows: emNegrito,
  };
}

function abaDoResumo(dados: DadosDoOrcamento): XlsxSheet {
  const rows: (readonly XlsxCell[])[] = groupBudget(dados.linhas).map((disciplina) => [
    disciplina.name,
    disciplina.material,
    disciplina.labor,
    disciplina.total,
    disciplina.linesOutOfTotal,
  ]);
  const resumo = summarizeBudget(dados.linhas);
  const total = rows.length;
  rows.push(['Total', resumo.material, resumo.labor, resumo.total, resumo.linesOutOfTotal]);

  // Um respiro, e as notas de onde o número veio: uma planilha que circula semanas depois de
  // gerada precisa dizer de que obra, de quando e de que preços ela é.
  rows.push([]);
  rows.push(['Obra', dados.obra]);
  rows.push(['Gerado em', dataEHora(dados.geradoEm)]);
  if (dados.periodoDosPrecos !== undefined) {
    rows.push([
      'Período dos preços',
      `${formatBusinessDate(dados.periodoDosPrecos.from)} a ${formatBusinessDate(dados.periodoDosPrecos.to)}`,
    ]);
  }
  if (dados.mesDaMaoDeObra !== undefined) {
    rows.push(['Mês da mão de obra', mesPorExtenso(dados.mesDaMaoDeObra)]);
  }
  rows.push(['Base', 'Custo direto, sem BDI']);
  const servicos = groupBudget(dados.linhas).flatMap((disciplina) => disciplina.services);
  const aprovados = servicos.filter(
    (servico) => dados.aprovados?.has(serviceLockKey(servico.key)) === true,
  ).length;
  if (aprovados > 0) {
    rows.push([
      'Serviços aprovados',
      `${String(aprovados)} de ${String(servicos.length)} — a quantidade e o preço da aprovação`,
    ]);
  }

  return {
    name: 'Resumo',
    headers: CABECALHO_DO_RESUMO,
    rows,
    widths: [34, 18, 18, 18, 22],
    formats: [undefined, DINHEIRO, DINHEIRO, DINHEIRO, '0'],
    boldRows: [total],
    // As notas abaixo da tabela não são dados para filtrar.
    filter: false,
  };
}

/** As duas abas da pasta: o detalhe e o resumo. */
export function orcamentoEmPlanilha(dados: DadosDoOrcamento): readonly XlsxSheet[] {
  return [abaDoDetalhe(dados.linhas, dados.aprovados), abaDoResumo(dados)];
}

/** `"orcamento-park-europa-2026-10-13.xlsx"` — o nome diz de que obra é e de quando. */
export function nomeDoArquivoDoOrcamento(obra: string, agora: Date): string {
  const dois = (valor: number): string => String(valor).padStart(2, '0');
  // O dia de quem gerou, e não o do UTC: depois das 21h em Brasília o UTC já é amanhã.
  const dia = `${String(agora.getFullYear())}-${dois(agora.getMonth() + 1)}-${dois(agora.getDate())}`;
  const limpo = obra
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return `orcamento-${limpo === '' ? 'obra' : limpo}-${dia}.xlsx`;
}
